import React, { Fragment, useState } from "react";
import Modal from "components/Modal";
import ModalContent from "components/ModalContent";
import ModalActions from "components/ModalActions";
import dynamic from "next/dynamic";
import Button from "components/Form/fields/Button";
import { BUTTON_CLASS_CONTEXT, UI_THEME } from "utils/constants/ui";
import { getIconForUID } from "components/FormerEditor/utils";
import { COLORS } from "utils/styles";
import { DEFAULT_CONTACTLESS_IMG } from "constants/contactless";
import Toggle from "react-toggle";
import { getToggleProps } from "utils/form";
import { useHasTestClientKey, useIsAuthed } from "utils/selectors";
import { useShortLinkCheckoutUrl } from "utils/hooks/shortLink";
import { getCodeDownloadFilename } from "utils/contactless";
import Analytics, { QR_CODE_DOWNLOAD } from "utils/analytics";
import ErrorBoundary from "components/ErrorBoundary";

const DEFAULT_CLASSES = {
  container:
    "pa3 ba hairline-1 br2 w-100 tc flex flex-row justify-between items-center",
  linkWrapper:
    "flex flex-row w-100 items-center justify-center justify-start-ns",
  copy: "f6 pl2"
};
const DEFAULT_COPY = "Contactless";

const CodeLinkLoader = () => (
  <div className="h3 w3 pa2 bg-white br2">
    <div className="shimmer w-100 h-100"></div>
  </div>
);
const QRCodeStyled = dynamic(() => import("components/QRCodeStyled"), {
  ssr: false,
  loading: CodeLinkLoader
});

const CodeLinkPreview = ({
  href,
  shortId,
  name,
  image = DEFAULT_CONTACTLESS_IMG,
  size = 128,
  customClasses,
  copy,
  disableTestToggle,
  code
}) => {
  const [showModal, setShowModal] = useState(false);
  const [downloadCode, setDownloadCode] = useState({});
  const isAuthed = useIsAuthed();
  const [testMode, setTestMode] = useState(!isAuthed);

  const checkoutUrl = useShortLinkCheckoutUrl({
    test: testMode,
    shortId
  });

  const ctxCheckoutUrl = href || checkoutUrl;
  const hasTestClientKey = useHasTestClientKey();

  const actionNode =
    hasTestClientKey && !disableTestToggle ? (
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row justify-between items-center">
          <label className="f6 fw5 db pr2">Test mode</label>
          <Toggle
            {...getToggleProps({
              active: testMode,
              setFieldValue: (_, active) => setTestMode(active)
            })}
          />
        </div>
      </div>
    ) : null;

  const Icon = getIconForUID("download");
  const classes = {
    ...DEFAULT_CLASSES,
    ...customClasses
  };

  const triggerCodeDownload = () => {
    if (downloadCode && downloadCode.code) {
      if (downloadCode.ext) {
        Analytics.track(QR_CODE_DOWNLOAD, {
          short_id: shortId,
          test: testMode
        });

        downloadCode.code.download({
          extension: downloadCode.ext,
          name: getCodeDownloadFilename({
            test: testMode,
            name: downloadCode.name
          })
        });
      } else {
        console.error("No file extensions for QR code download");
      }
    } else {
      console.error("No QR code instance");
    }
  };
  const hasMinDownloadProps =
    downloadCode && downloadCode.code && downloadCode.ext;
  const downloadCodeButtonProps = {
    onClick: triggerCodeDownload,
    tag: "button",
    theme: UI_THEME.SLIM,
    disabled: !hasMinDownloadProps
  };

  const commonQRCodeProps = {
    ...code,
    url: ctxCheckoutUrl,
    hideDownloadIcon: true,
    name,
    image,
    test: testMode
  };
  const inlineQRCodeProps = {
    ...commonQRCodeProps,
    size,
    hideDownloadIcon: true,
    hoistCode: (hoistedValues) => {
      setDownloadCode(hoistedValues);
    }
  };

  const modalQRCodeProps = {
    ...commonQRCodeProps,
    size: 256
  };
  const fallback = <span />;

  return (
    <ErrorBoundary fallback={fallback}>
      <div className={classes.container}>
        <div className={classes.linkWrapper}>
          <QRCodeStyled {...inlineQRCodeProps} />
          <div className={classes.copy}>{copy || DEFAULT_COPY}</div>
        </div>
        <div className="flex flex-row items-center flex-shrink-0 pt3 pt0-ns">
          <div className="pr2 mr2 br hairline-1">{actionNode}</div>
          <div className="pr2">
            <Button {...downloadCodeButtonProps}>
              <div className="pt1">
                <Icon size={20} color={COLORS.white} />
              </div>
            </Button>
          </div>
          <Button
            theme="slim"
            copy="Show"
            onClick={() => setShowModal(true)}
            classes={{
              contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
            }}
          ></Button>
        </div>
      </div>
      {showModal && (
        <Modal showModal={showModal} onClose={() => setShowModal(false)}>
          <ModalContent>
            {() => (
              <div className="w-100 pa3">
                <div className="w-100 flex flex-column items-center">
                  <QRCodeStyled {...modalQRCodeProps} />
                  <ModalActions
                    actions={[
                      {
                        cta: "Close",
                        theme: "slim",
                        onClick: () => {
                          setShowModal(false);
                        }
                      },
                      {
                        cta: "Download",
                        tag: "button",
                        theme: "slim",
                        onClick: triggerCodeDownload
                      }
                    ]}
                  />
                </div>
              </div>
            )}
          </ModalContent>
        </Modal>
      )}
    </ErrorBoundary>
  );
};

export default CodeLinkPreview;
